import React, { useMemo, useCallback } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { apiSchemas } from '@trinity/management-app-client';

type FormSchema = apiSchemas.FormSchema;
type FieldDefinition = apiSchemas.FieldDefinition;
type SectionDefinition = apiSchemas.SectionDefinition;
import { DynamicFormDashboard } from './DynamicForm.dashboard';
import { formatFieldValue, formatSubmittedTimestamp } from './DynamicForm.form.utils';
import {
  FormContainer,
  SubmittedWrapper,
  SubmittedHeader,
  SubmittedHeaderContent,
  SubmittedTitle,
  SubmittedToggleIcon,
  SubmittedContent,
  SubmittedFieldGrid,
  SubmittedFieldRow,
  SubmittedFieldLabel,
  SubmittedFieldValue,
  SubmittedEmptyState,
  SubmittedSectionContainer,
  SubmittedSectionTitle,
  SubmittedSectionBox,
  ActionButtonContainer,
  ActionButton,
} from './DynamicForm.styles.index';

const CONSTANTS = {
  MESSAGES: {
    EDIT: 'Edit',
  },
} as const;

interface SubmittedViewProps {
  formConfig: FormSchema;
  formData: Record<string, unknown>;
  isDraft?: boolean;
  isSubmitted?: boolean;
  isSubmitting?: boolean;
  showButtons?: boolean;
  onEdit?: () => void;
  submittedAt?: string | number | Date;
  draftAt?: string | number | Date;
  className?: string;
  style?: React.CSSProperties;
  sections: SectionDefinition[];
  fieldsBySection: Map<string, FieldDefinition[]>;
  fieldsWithoutSection: FieldDefinition[];
}

const renderSubmittedField = (
  field: FieldDefinition,
  formData: Record<string, unknown>,
): React.ReactNode => {
  // Try field name first, then field label (for backwards compatibility with drafts saved with label keys)
  const fieldValue = formData[field.name] ?? formData[field.label];
  const displayValue = formatFieldValue(field, fieldValue);

  return (
    <SubmittedFieldRow key={field.id}>
      <SubmittedFieldLabel>
        {field.label}
        {field.required && <span className="required-asterisk">*</span>}
      </SubmittedFieldLabel>
      <SubmittedFieldValue>
        {displayValue}
      </SubmittedFieldValue>
    </SubmittedFieldRow>
  );
};

const SubmittedExpandedContent: React.FC<{
  formConfig: FormSchema;
  formData: Record<string, unknown>;
  metaText: string;
  hasSectionContent: boolean;
  hasStandaloneFields: boolean;
  hasContent: boolean;
  renderedSections: React.ReactNode[];
  fieldsWithoutSection: FieldDefinition[];
  contentId: string;
  onEdit?: () => void;
  showButtons?: boolean;
  isDraft?: boolean;
  isSubmitting?: boolean;
}> = ({
  formConfig,
  formData,
  metaText,
  hasSectionContent,
  hasStandaloneFields,
  hasContent,
  renderedSections,
  fieldsWithoutSection,
  contentId,
  onEdit,
  showButtons,
  isDraft,
  isSubmitting,
}) => (
  <SubmittedContent id={contentId}>
    <SubmittedSectionBox>
      <SubmittedSectionContainer>
        {!!metaText && (
          <SubmittedFieldGrid>
            <SubmittedFieldRow>
              <SubmittedFieldValue>{metaText}</SubmittedFieldValue>
            </SubmittedFieldRow>
          </SubmittedFieldGrid>
        )}
      </SubmittedSectionContainer>
    </SubmittedSectionBox>
    {hasContent ? (
      <>
        {formConfig.richContentEndpoint && (
          <DynamicFormDashboard />
        )}
        {hasSectionContent ? (
          renderedSections
        ) : (
          <SubmittedSectionBox>
            <SubmittedFieldGrid>
              {fieldsWithoutSection.map(field => renderSubmittedField(field, formData))}
            </SubmittedFieldGrid>
          </SubmittedSectionBox>
        )}
        {hasSectionContent && hasStandaloneFields && (
          <SubmittedSectionBox>
            <SubmittedFieldGrid>
              {fieldsWithoutSection.map(field => renderSubmittedField(field, formData))}
            </SubmittedFieldGrid>
          </SubmittedSectionBox>
        )}
      </>
    ) : (
      <SubmittedEmptyState>No form data available</SubmittedEmptyState>
    )}
    {onEdit && showButtons && isDraft && (
      <ActionButtonContainer>
        <ActionButton
          variant='primary'
          onClick={() => {
            onEdit();
          }}
          disabled={isSubmitting}
          type="button"
          icon={EditIcon}
        >
          {CONSTANTS.MESSAGES.EDIT}
        </ActionButton>
      </ActionButtonContainer>
    )}
  </SubmittedContent>
);

export const SubmittedView: React.FC<SubmittedViewProps> = ({
  formConfig,
  formData,
  isDraft = false,
  isSubmitted = false,
  isSubmitting = false,
  showButtons = true,
  onEdit,
  submittedAt,
  draftAt,
  className,
  style,
  sections,
  fieldsBySection,
  fieldsWithoutSection,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  const formattedSubmittedAt = useMemo(() => formatSubmittedTimestamp(submittedAt), [submittedAt]);
  const formattedDraftAt = useMemo(() => formatSubmittedTimestamp(draftAt), [draftAt]);

  const summaryTitle = useMemo(() => {
    const baseTitle = formConfig.name || (isDraft ? 'Draft Form' : 'Submitted Form');
    return isDraft ? `${baseTitle} (Draft)` : `${baseTitle} submitted`;
  }, [formConfig.name, isDraft]);

  const contentId = useMemo(() => {
    return isDraft
      ? `draft-form-content-${formConfig.id}`
      : `submitted-form-content-${formConfig.id}`;
  }, [formConfig.id, isDraft]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const renderedSections = useMemo(() => {
    return sections.map(section => {
      const sectionFields = fieldsBySection.get(section.id) || [];
      if (sectionFields.length === 0) return null;

      return (
        <SubmittedSectionBox key={section.id}>
          <SubmittedSectionContainer>
            <SubmittedSectionTitle>{section.title}</SubmittedSectionTitle>
            <SubmittedFieldGrid>
              {sectionFields.map(field => renderSubmittedField(field, formData))}
            </SubmittedFieldGrid>
          </SubmittedSectionContainer>
        </SubmittedSectionBox>
      );
    });
  }, [sections, fieldsBySection, formData]);

  const hasSectionContent = renderedSections.some(section => section !== null);
  const hasStandaloneFields = fieldsWithoutSection.length > 0;
  const hasContent = hasSectionContent || hasStandaloneFields;

  let metaText: string;
  if (isDraft) {
    metaText = formattedDraftAt ? `Draft saved: ${formattedDraftAt}` : 'Draft';
  } else {
    metaText = formattedSubmittedAt ? `Date: ${formattedSubmittedAt}` : 'Date unavailable';
  }

  return (
    <FormContainer
      className={className}
      style={{
        minWidth: '600px',
        maxWidth: '600px',
        margin: '0 auto',
        ...style,
      }}
    >
      <SubmittedWrapper>
        <SubmittedHeader
          type="button"
          onClick={toggleExpanded}
          aria-expanded={isExpanded}
          aria-controls={contentId}
          expanded={isExpanded}
        >
          <SubmittedHeaderContent>
            <SubmittedTitle>{summaryTitle}</SubmittedTitle>
          </SubmittedHeaderContent>
          <SubmittedToggleIcon expanded={isExpanded}>
            <ExpandMoreIcon fontSize="small" />
          </SubmittedToggleIcon>
        </SubmittedHeader>

        {isExpanded && (
          <SubmittedExpandedContent
            formConfig={formConfig}
            formData={formData}
            metaText={metaText}
            hasSectionContent={hasSectionContent}
            hasStandaloneFields={hasStandaloneFields}
            hasContent={hasContent}
            renderedSections={renderedSections}
            fieldsWithoutSection={fieldsWithoutSection}
            contentId={contentId}
            onEdit={onEdit}
            showButtons={showButtons}
            isDraft={isDraft}
            isSubmitting={isSubmitting}
          />
        )}
      </SubmittedWrapper>
    </FormContainer>
  );
};

